import { Banknote, BadgeDollarSign, CheckCircle2, FileCheck2, Landmark, Pencil, Plus, Send, Trash2, UsersRound, Search } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NextStepCard } from "../components/NextStepCard";
import { StatCard } from "../components/StatCard";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { clients as clientsApi, dashboard, type ClientListItem, type DashboardStats } from "../lib/api";
import { clientInitials } from "../lib/clientName";

// Animates an integer from 0 to `value` over 400ms with ease-out-quint.
// Respects prefers-reduced-motion (snaps to final value, no animation).
function CountUp({ value }: { value: number }) {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setN(value); return; }
    const start = performance.now();
    const duration = 400;
    let raf: number;
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 5); // quint out
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{n.toLocaleString()}</>;
}

export default function DashboardPage() {
  const nav = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [clientList, setClientList] = useState<ClientListItem[]>([]);
  // Rail filter: with a growing book of clients the dashboard list gets long —
  // this narrows by name / id / assigned agent without leaving the page.
  const [railFilter, setRailFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try { setStats(await dashboard.stats()); } catch {}
      try {
        // Show everything in flight (onboarding + active + needs_review). Filter UI lives on /clients.
        // 200 is the API cap; the rail itself filters + scrolls, so a growing
        // book of clients stays reachable without leaving the dashboard.
        const res = await clientsApi.list({ limit: 200 });
        const inFlight = (res.items || []).filter(
          c => c.status === "onboarding" || c.status === "active" || c.status === "needs_review"
        );
        setClientList(inFlight);
        if (inFlight[0]) setSelectedId(inFlight[0].id);
      } catch {}
    })();
  }, []);

  const selected = clientList.find((c) => c.id === selectedId) || clientList[0];

  const money = (n: number) =>
    `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const visibleClients = railFilter.trim()
    ? clientList.filter((c) => {
        const hay = `${c.legal_name ?? ""} ${c.id} ${c.assigned_agent ?? ""}`.toLowerCase();
        return hay.includes(railFilter.trim().toLowerCase());
      })
    : clientList;

  return (
    <Shell title="Alief Funding Hub" subtitle="Business funding platform">
      {clientList.length === 0 && (
        <NextStepCard
          label="Get started"
          steps={[
            {
              label: "Add a client to begin",
              state: "next",
              actionLabel: "+ New client",
              onAction: () => nav("/clients/new"),
            },
            { label: "Send intake link (capital, goal, business, location)", state: "todo" },
            { label: "Import credit report via their monitoring signup", state: "todo" },
            { label: "Run funding qualification", state: "todo" },
            { label: "Send the right contract, match lenders, get the bag", state: "todo" },
          ]}
        />
      )}

      <section className="stats-grid">
        <StatCard
          label="Active clients"
          value={stats ? <CountUp value={stats.active_clients} /> : <span className="ne-skeleton">00</span>}
          detail={`${stats?.onboarding ?? 0} in onboarding`}
          icon={<UsersRound size={20} aria-hidden="true" />}
        />
        <StatCard
          label="Funding ready"
          value={stats ? <CountUp value={stats.funding_ready} /> : <span className="ne-skeleton">00</span>}
          detail={`${stats?.contracts_awaiting_signature ?? 0} contracts awaiting signature`}
          icon={<CheckCircle2 size={20} aria-hidden="true" />}
        />
        <StatCard
          label="Deals in pipeline"
          value={stats ? <CountUp value={stats.deals_in_pipeline} /> : <span className="ne-skeleton">00</span>}
          detail={`${stats?.brm_referrals_this_month ?? 0} sent to BRMs this month`}
          icon={<Banknote size={20} aria-hidden="true" />}
        />
        <StatCard
          label="Funded this month"
          value={stats ? money(stats.amount_funded_this_month) : <span className="ne-skeleton">$0</span>}
          detail={`${money(stats?.expected_commission ?? 0)} expected commission in pipeline`}
          icon={<BadgeDollarSign size={20} aria-hidden="true" />}
        />
      </section>

      <section className="main-grid">
        <div className="panel client-list-panel">
          <div className="panel-header">
            <div>
              <h2>Clients</h2>
            </div>
            <button className="ghost-button" onClick={() => nav("/clients/new")}>
              <Plus size={16} aria-hidden="true" /> Add
            </button>
          </div>
          {clientList.length > 6 && (
            <label className="search rail-search">
              <Search size={15} aria-hidden="true" />
              <input
                placeholder="Find a client…"
                aria-label="Filter the client list by name or agent"
                value={railFilter}
                onChange={(e) => setRailFilter(e.target.value)}
              />
            </label>
          )}
          <div className="client-list client-list--scroll">
            {visibleClients.length === 0 && (
              <p className="muted small">{clientList.length === 0 ? "No active clients yet." : "No client matches that search."}</p>
            )}
            {visibleClients.map((c) => (
              <button
                key={c.id}
                className={`client-row ${selected?.id === c.id ? "selected" : ""}`}
                onClick={() => setSelectedId(c.id)}
              >
                <div className="avatar" title={c.legal_name ?? undefined}>{clientInitials(c.legal_name)}</div>
                <div className="client-main">
                  <strong>{c.legal_name}</strong>
                  <span>{c.id.slice(0, 8)} · {c.assigned_agent || "Unassigned"}</span>
                </div>
                <StatusPill status={c.status} />
              </button>
            ))}
          </div>
        </div>

        {selected && (
          <div className="panel profile-panel">
            <div className="profile-header">
              <div>
                <h2>{selected.legal_name ?? "(unnamed)"}</h2>
                <span>{selected.phone || "—"} · {selected.email}</span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <StatusPill status={selected.status} />
                <button
                  className="ghost-button"
                  onClick={() => nav(`/clients/${selected.id}`)}
                  title="Edit client"
                  aria-label="Edit client"
                >
                  <Pencil size={14} aria-hidden="true" /> Edit
                </button>
                <button
                  className="ghost-button"
                  onClick={async () => {
                    if (!confirm(`Delete ${selected.legal_name ?? "this client"}? This soft-deletes (status → churned) and can be restored.`)) return;
                    try {
                      await clientsApi.remove(selected.id);
                      setClientList(prev => prev.filter(c => c.id !== selected.id));
                      setSelectedId(null);
                    } catch (e: any) {
                      alert(`Delete failed: ${e?.message || "unknown error"}`);
                    }
                  }}
                  title="Delete client"
                  aria-label="Delete client"
                  style={{ color: "var(--ne-red)" }}
                >
                  <Trash2 size={14} aria-hidden="true" /> Delete
                </button>
              </div>
            </div>

            <div className="profile-tabs" role="tablist" aria-label="Client profile sections">
              <button
                className="selected"
                role="tab"
                aria-selected={true}
                aria-controls="client-overview-panel"
                id="client-overview-tab"
              >
                Overview
              </button>
              <button role="tab" aria-selected={false} onClick={() => nav(`/clients/${selected.id}`)}>Qualification</button>
              <button role="tab" aria-selected={false} onClick={() => nav(`/clients/${selected.id}`)}>Lenders</button>
              <button role="tab" aria-selected={false} onClick={() => nav(`/clients/${selected.id}`)}>Documents</button>
            </div>

            <section
              id="client-overview-panel"
              role="tabpanel"
              aria-labelledby="client-overview-tab"
            >
              <h3 className="sr-only">Client metrics</h3>
              <div className="profile-metrics">
                <div><p>Start score</p><strong>{selected.score_start ?? "—"}</strong></div>
                <div><p>Current score</p><strong>{selected.score_current ?? "—"}</strong></div>
                <div><p>Capital requested</p><strong>{(selected as any).capital_requested ? money(Number((selected as any).capital_requested)) : "—"}</strong></div>
                <div><p>Business state</p><strong>{(selected as any).business_state ?? "—"}</strong></div>
              </div>

              <div className="split-section">
                <section className="sub-panel">
                  <div className="sub-heading"><Landmark size={18} aria-hidden="true" /><h3>Funding path</h3></div>
                  <p className="body-copy">
                    Import the report, run qualification, then match lenders.
                  </p>
                  <button className="wide-button" onClick={() => nav(`/clients/${selected.id}`)}>Open client</button>
                </section>
                <section className="sub-panel">
                  <div className="sub-heading"><FileCheck2 size={18} aria-hidden="true" /><h3>Contract</h3></div>
                  <p className="body-copy">
                    Funding agreement: {selected.service_agreement_signed ? "Signed" : "Not signed yet — send after qualification"}.
                  </p>
                  <button className="wide-button" onClick={() => nav(`/clients/${selected.id}`)}>Manage</button>
                </section>
              </div>
            </section>
          </div>
        )}

        <div className="panel action-panel">
          <div className="panel-header">
            <div>
              <h2>Next steps</h2>
              <span className="muted small">
                {selected ? `For ${selected.legal_name}` : "Pick a client to see their next actions"}
              </span>
            </div>
          </div>
          <div className="task-stack">
            <article>
              <CheckCircle2 size={18} aria-hidden="true" />
              <div>
                <strong>{selected?.next_action || "Review onboarding pipeline"}</strong>
                <span>Assigned to {selected?.assigned_agent || "team"}</span>
              </div>
            </article>
            <article>
              <Landmark size={18} aria-hidden="true" />
              <div>
                <strong>Lender database</strong>
                <span>{stats?.active_lenders ?? 0} active lenders — keep boxes current as banks open and close</span>
              </div>
            </article>
            <article>
              <Send size={18} aria-hidden="true" />
              <div>
                <strong>BRM handoff</strong>
                <span>One click packages the client file to your bank rep</span>
              </div>
            </article>
          </div>
        </div>
      </section>
    </Shell>
  );
}
